// Unified Distribution Models

// Base Distribution Record
export interface DistributionRecord {
  id: string;
  marketerId: string;
  marketerName: string;
  marketerUsername: string;
  productId: string;
  productName: string;
  productBarcode?: string;
  productImages?: string[]; // Add product images
  quantity: number;
  hasSerial: boolean;
  serials?: string[];
  deliveredAt: string;
  deliveredBy: string;
  deliveredByName: string;
  status: DistributionStatus;
  notes?: string;
  location?: string;
  cost?: number;
}

export enum DistributionStatus {
  PENDING = 'PENDING',
  DELIVERED = 'DELIVERED',
  PARTIALLY_DELIVERED = 'PARTIALLY_DELIVERED',
  CANCELLED = 'CANCELLED',
  RETURNED = 'RETURNED'
}

// Marketer Information (extended from marketers feature)
export interface DistributionMarketer {
  id: string;
  username: string;
  fullName: string;
  email?: string;
  phone?: string;
  area?: string;
  isActive: boolean;
  totalDeliveries: number;
  lastDeliveryDate?: string;
}

// Product Information (for distribution context)
export interface DistributionProduct {
  id: string;
  name: string;
  barcode: string;
  hasSerial: boolean;
  availableQuantity: number;
  price: number;
  brand?: string; // Add brand field from API
  type?: string; // Add type field from API
  warehouseLocation?: string; // Add warehouse_location field from API
  category?: string;
  description?: string;
  images?: string[]; // Add product images
}

// Available Serial for Distribution
export interface AvailableSerial {
  id: string;
  productId: string;
  serialNumber: string;
  locationId?: string;
  locationName?: string;
  status: SerialStatus;
  createdAt: string;
}

export enum SerialStatus {
  AVAILABLE = 'AVAILABLE',
  ASSIGNED = 'ASSIGNED',
  SOLD = 'SOLD'
}

// Delivery Forms
export interface CreateDeliveryForm {
  marketerId: string;
  productId: string;
  quantity: number;
  serials?: string[];
  scheduledDate?: string;
  deliveredBy?: string; // Add deliveredBy field
}

export interface UpdateDeliveryForm {
  quantity?: number;
  serials?: string[];
  status?: DistributionStatus;
  notes?: string;
  deliveredAt?: string;
}

// Distribution Statistics
export interface DistributionStats {
  totalDeliveries: number;
  totalQuantityDelivered: number;
  pendingDeliveries: number;
  completedDeliveries: number;
  totalMarketers: number;
  activeMarketers: number;
  topMarketers: TopMarketer[];
  topProducts: TopProduct[];
  deliveriesByStatus: DeliveryStatusCount[];
  deliveriesByPeriod: DeliveryPeriodCount[];
}

export interface TopMarketer {
  marketerId: string;
  marketerName: string;
  deliveryCount: number;
  totalQuantity: number;
  lastDelivery: string;
}

export interface TopProduct {
  productId: string;
  productName: string;
  deliveryCount: number;
  totalQuantity: number;
  lastDelivery: string;
}

export interface DeliveryStatusCount {
  status: DistributionStatus;
  count: number;
  percentage: number;
}

export interface DeliveryPeriodCount {
  period: string; // 'today', 'this_week', 'this_month', etc.
  count: number;
  quantity: number;
}

// Filters
export interface DistributionFilters {
  marketerId?: string;
  productId?: string;
  status?: DistributionStatus;
  deliveredBy?: string;
  dateFrom?: string;
  dateTo?: string;
  hasSerial?: boolean;
  location?: string;
  minQuantity?: number;
  maxQuantity?: number;
}

export interface MarketerFilters {
  area?: string;
  isActive?: boolean;
  hasDeliveries?: boolean;
  searchTerm?: string;
}

export interface ProductFilters {
  category?: string;
  hasSerial?: boolean;
  minPrice?: number;
  maxPrice?: number;
  availableOnly?: boolean;
  searchTerm?: string;
}

// Pagination and Sorting
export interface PaginationOptions {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}
